type StorageAccess = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Storage can throw at property access or on any operation (privacy/quota).
 * After a failure, keep this document's state in memory. In particular, never
 * read a stale persisted token again after a failed logout removal.
 */
export function createResilientStorage(getStorage: () => StorageAccess) {
  const memory = new Map<string, string>();
  let unavailable = false;
  return {
    getItem(key: string): string | null {
      if (!unavailable) {
        try {
          const value = getStorage().getItem(key);
          if (value === null) memory.delete(key);
          else memory.set(key, value);
          return value;
        } catch {
          unavailable = true;
        }
      }
      return memory.get(key) ?? null;
    },
    setItem(key: string, value: string) {
      memory.set(key, value);
      if (!unavailable) {
        try {
          getStorage().setItem(key, value);
        } catch {
          unavailable = true;
        }
      }
    },
    removeItem(key: string) {
      memory.delete(key);
      try {
        // A write can fail because of quota while removal is still available.
        // Always attempt deletion so an old persisted session cannot return
        // after the page is reloaded.
        getStorage().removeItem(key);
      } catch {
        unavailable = true;
      }
    },
  };
}

export const browserStorage = createResilientStorage(() => window.localStorage);
